import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useSubscription } from '@/hooks/useSubscription';
import { useTheme } from 'next-themes';
import { syncStorage } from '@/lib/storage';

interface CinemaModeContextType {
  cinemaMode: boolean;
  setCinemaMode: (enabled: boolean) => void;
  canAccessCinemaModePlus: boolean;
  prefersReducedMotion: boolean;
}

const CinemaModeContext = createContext<CinemaModeContextType | undefined>(undefined);

const CINEMA_MODE_KEY = 'orbit-cinema-mode';

export const CinemaModeProvider = ({ children }: { children: ReactNode }) => {
  const { isPro } = useSubscription();
  const { setTheme, resolvedTheme } = useTheme();
  
  const [cinemaMode, setCinemaModeState] = useState(() => {
    if (typeof window === 'undefined') return false;
    return syncStorage.get(CINEMA_MODE_KEY) === 'true';
  });

  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  const setCinemaMode = useCallback((enabled: boolean) => {
    setCinemaModeState(enabled);
    syncStorage.set(CINEMA_MODE_KEY, String(enabled));
    
    // Force dark mode when cinema mode is enabled (for ALL themes)
    if (enabled) {
      setTheme('dark');
    }
  }, [setTheme]);

  // Force dark mode whenever cinema mode is active
  useEffect(() => {
    if (cinemaMode && resolvedTheme !== 'dark') {
      setTheme('dark');
    }
  }, [cinemaMode, resolvedTheme, setTheme]);

  // Apply data attributes for CSS targeting - Cinema Mode now includes all premium effects
  useEffect(() => {
    const root = document.documentElement;
    if (cinemaMode) {
      root.setAttribute('data-cinema-mode', 'true');
      // Cinema Mode now always includes premium effects (no separate plus mode)
      root.setAttribute('data-cinema-mode-plus', 'true');
    } else {
      root.removeAttribute('data-cinema-mode');
      root.removeAttribute('data-cinema-mode-plus');
    }
  }, [cinemaMode]);

  return (
    <CinemaModeContext.Provider
      value={{
        cinemaMode,
        setCinemaMode,
        canAccessCinemaModePlus: isPro,
        prefersReducedMotion,
      }}
    >
      {children}
    </CinemaModeContext.Provider>
  );
};

export const useCinemaMode = () => {
  const context = useContext(CinemaModeContext);
  if (!context) {
    throw new Error('useCinemaMode must be used within a CinemaModeProvider');
  }
  return context;
};